import React, { useEffect, useRef, useState } from 'react';
import { Videos } from '../../../../assets/svgs/Videos';
import { selectTodayStudyHistoryData } from '../../../../slices/todayStudyHistory/todayStudyHistorySlice';
import { useSelector } from 'react-redux';
import { useAccess } from '../../../../hooks/useAccess';
import { ListBullets, Upload } from '@phosphor-icons/react';

import { useDailyUploadLimit } from '../../../../hooks/useDailyUploadLimit';
import { useDailyChatLimit } from '../../../../hooks/useDailyChatLimit';
import { AI } from '../../../../assets/svgs/AI';
import ProgressBar from '../../Global/ProgressBar';
import Tooltip from '@/components/ui/tooltip';

const DailyLimits: React.FC = () => {
  const todayStudyHistory = useSelector(selectTodayStudyHistoryData);
  const { limits } = useAccess();
  // When rendered inside the hover-expanding navbar, portal the tooltips into
  // the navbar itself so their content stays within the `.navbar:hover`
  // subtree. Otherwise the default body portal steals the hover and collapses
  // the sidebar (ORA-135). Elsewhere (e.g. the Upgrade modal) there is no
  // `.navbar` ancestor and the tooltips fall back to the default body portal.
  const rootRef = useRef<HTMLDivElement>(null);

  const [tooltipContainer, setTooltipContainer] = useState<HTMLElement | null>(
    null
  );

  useEffect(() => {
    setTooltipContainer(
      rootRef.current?.closest<HTMLElement>('.navbar') ?? null
    );
  }, []);

  const {
    uploadsToday,
    limit: uploadLimit,
    isUnlimited,
  } = useDailyUploadLimit();

  const {
    responsesToday,
    limit: chatLimit,
    isUnlimited: isChatUnlimited,
  } = useDailyChatLimit();

  const todayQBank = Math.min(
    todayStudyHistory?.qbank.total || 0,
    limits.qbank
  );

  const todayVideos = Math.min(
    todayStudyHistory?.videos.started || 0,
    limits.videos
  );

  const uploadProgressPercent =
    uploadLimit && !isUnlimited
      ? Math.min(Math.round(((uploadsToday ?? 0) / uploadLimit) * 100), 100)
      : 100;

  const uploadLimitLabel =
    uploadLimit && !isUnlimited
      ? `${Math.min(uploadsToday ?? 0, uploadLimit)} of ${uploadLimit}`
      : `${uploadsToday ?? 0} (Unlimited)`;

  const chatProgressPercent =
    chatLimit && !isChatUnlimited
      ? Math.min(Math.round(((responsesToday ?? 0) / chatLimit) * 100), 100)
      : 100;

  const chatLimitLabel =
    chatLimit && !isChatUnlimited
      ? `${Math.min(responsesToday ?? 0, chatLimit)} of ${chatLimit}`
      : `${responsesToday ?? 0} (Unlimited)`;

  return (
    <div className="daily-limits" ref={rootRef}>
      <div className="daily-limits_title">Today's Activity</div>
      <Tooltip
        title={'Documents uploaded today.'}
        enterTouchDelay={0}
        enterDelay={250}
        container={tooltipContainer}
      >
        <div className="daily-limits_item">
          <div className="label">
            <Upload />
            <span>Uploads</span>
          </div>
          <ProgressBar
            value={uploadProgressPercent}
            barColor="var(--error)"
            showLabel={true}
            label={uploadLimitLabel}
            ariaLabel={`Uploads today: ${uploadLimitLabel}`}
          />
        </div>
      </Tooltip>
      <Tooltip
        title="AI chat messages sent today."
        enterTouchDelay={0}
        enterDelay={250}
        container={tooltipContainer}
      >
        <div className="daily-limits_item">
          <div className="label">
            <AI />
            <span>AI Chat</span>
          </div>
          <ProgressBar
            value={chatProgressPercent}
            barColor="var(--error)"
            showLabel={true}
            label={chatLimitLabel}
            ariaLabel={`AI Chat messages today: ${chatLimitLabel}`}
          />
        </div>
      </Tooltip>
      <Tooltip
        title="Videos watched today."
        enterTouchDelay={0}
        enterDelay={250}
        container={tooltipContainer}
      >
        <div className="daily-limits_item">
          <div className="label">
            <Videos />
            <span>Videos</span>
          </div>
          <ProgressBar
            value={Math.round((todayVideos / limits.videos) * 100)}
            barColor="var(--error)"
            showLabel={true}
            label={`${todayVideos} of ${limits.videos}`}
            ariaLabel={`Videos watched today: ${todayVideos} of ${limits.videos}`}
          />
        </div>
      </Tooltip>
      <Tooltip
        title="QBank questions answered today."
        enterTouchDelay={0}
        enterDelay={250}
        container={tooltipContainer}
      >
        <div className="daily-limits_item">
          <div className="label">
            <ListBullets />
            <span>QBank</span>
          </div>
          <ProgressBar
            value={Math.round((todayQBank / limits.qbank) * 100)}
            barColor="var(--error)"
            showLabel={true}
            label={`${todayQBank} of ${limits.qbank}`}
            ariaLabel={`QBank questions answered today: ${todayQBank} of ${limits.qbank}`}
          />
        </div>
      </Tooltip>
    </div>
  );
};

export default DailyLimits;
